/**
 * VeraLokLogo — renders the approved VeraLok brand mark by directly
 * loading `/brand/veralok-logo.png` from the public folder. This is the
 * single source of truth for the mark; do not redesign or reinterpret
 * it in SVG. See `frontend/public/brand/veralok-logo.png`.
 *
 * The approved asset is a square lockup (mark on top, wordmark below).
 * Every consumer surface in the app renders the mark inline with a
 * separate `VeraLok` wordmark to fit tight headers/menus, so we clip
 * to just the mark region and add the wordmark next to it in code.
 * `showWordmark={false}` renders the mark alone.
 */
import React from "react";

const LOGO_SRC = "/brand/veralok-logo.png";

// The asset is a 1024×1024 square with the mark occupying roughly the
// top 62% and the wordmark the bottom. Clip to a top-anchored square
// of the requested size — the browser preserves crisp scaling.
function MarkOnly({ size }) {
  return (
    <span
      className="inline-block overflow-hidden align-middle"
      style={{ width: size, height: size, flex: "none" }}
      aria-hidden="true"
    >
      <img
        src={LOGO_SRC}
        alt=""
        draggable={false}
        style={{
          width: size,
          // 0.62 → the mark occupies the top 62% of the square asset.
          // Sizing the image larger than the frame and clipping via
          // overflow:hidden yields the mark with no wordmark bleed.
          height: `calc(${size}px / 0.62)`,
          objectFit: "cover",
          objectPosition: "top center",
          display: "block",
        }}
      />
    </span>
  );
}

export default function VeraLokLogo({
  size = 32,
  showWordmark = true,
  wordmarkColor,
  className = "",
}) {
  if (!showWordmark) {
    return (
      <span
        className={`inline-flex items-center ${className}`}
        aria-label="VeraLok"
        data-testid="vlk-logo"
      >
        <MarkOnly size={size} />
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-2 ${className}`}
      aria-label="VeraLok"
      data-testid="vlk-logo"
    >
      <MarkOnly size={size} />
      <span
        className="font-semibold tracking-tight"
        style={{
          fontFamily: "var(--vlk-font-display), Cabinet Grotesk, Manrope, ui-sans-serif, system-ui",
          fontSize: `${Math.max(15, Math.round(size * 0.62))}px`,
          color: wordmarkColor || "currentColor",
          letterSpacing: "-0.01em",
          lineHeight: 1,
        }}
      >
        VeraLok
      </span>
    </span>
  );
}
